import React, { useState } from "react";
import {
  Stack,
  Card,
  Text,
  Group,
  Button,
  Modal,
  Select,
  ActionIcon,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconPencil } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";

const toISODateString = date => {
  if (!date) return null;
  if (typeof date === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const SCHEDULE_TYPE_OPTIONS = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "BI_WEEKLY", label: "Bi-Weekly" },
];

function UpfrontPayScheduleSection({
  schedule,
  onScheduleChange,
  sellingYearId,
}) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  const [modalOpened, setModalOpened] = useState(false);
  const [editType, setEditType] = useState(null);
  const [editDate, setEditDate] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleOpenModal = () => {
    setEditType(schedule?.schedule_type ?? null);
    setEditDate(
      schedule?.starting_date ? new Date(schedule.starting_date) : null
    );
    setModalOpened(true);
  };

  const handleClose = () => {
    setModalOpened(false);
  };

  const handleSave = async () => {
    if (!editType || !editDate) return;

    try {
      setSaving(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const body = {
        schedule_type: editType,
        starting_date: toISODateString(editDate),
        selling_year_id: sellingYearId ? Number(sellingYearId) : undefined,
      };

      const response = await fetch(
        getApiEndpoint("/v1/ps/upfront-pay-schedule"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (response.ok) {
        const result = await response.json().catch(() => null);
        const data = result?.data ?? result ?? body;
        onScheduleChange?.(data);
        showSuccess("Upfront pay schedule saved!");
        handleClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to save upfront pay schedule"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to save upfront pay schedule");
    } finally {
      setSaving(false);
    }
  };

  const scheduleTypeLabel =
    SCHEDULE_TYPE_OPTIONS.find(o => o.value === schedule?.schedule_type)
      ?.label ?? null;

  const startingDateLabel = schedule?.starting_date
    ? new Date(schedule.starting_date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      })
    : null;

  return (
    <>
      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            {schedule ? (
              <>
                {scheduleTypeLabel && (
                  <Group gap="xs">
                    <Text size="sm" c="dimmed" fw={600}>
                      Schedule Type:
                    </Text>
                    <Text size="sm">{scheduleTypeLabel}</Text>
                  </Group>
                )}
                {startingDateLabel && (
                  <Group gap="xs">
                    <Text size="sm" c="dimmed" fw={600}>
                      Starting Date:
                    </Text>
                    <Text size="sm">{startingDateLabel}</Text>
                  </Group>
                )}
              </>
            ) : (
              <Text size="sm" c="dimmed">
                No schedule configured.
              </Text>
            )}
          </Stack>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={handleOpenModal}
            aria-label="Edit upfront pay schedule"
          >
            <IconPencil size={16} />
          </ActionIcon>
        </Group>
      </Card>

      <Modal
        opened={modalOpened}
        onClose={handleClose}
        title="Edit Upfront Pay Schedule"
        size="sm"
      >
        <Stack gap="md">
          <Select
            label="Schedule Type"
            placeholder="Select schedule type"
            data={SCHEDULE_TYPE_OPTIONS}
            value={editType}
            onChange={setEditType}
          />
          <DatePickerInput
            label="Starting Date"
            placeholder="Pick starting date"
            value={editDate}
            onChange={setEditDate}
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
              disabled={!editType || !editDate}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default UpfrontPayScheduleSection;
